import { GetProfileInfoResponse } from '@cash-web/protos/squareup/cash/cashdotapp/app.pb'
import { Money } from '@cash-web/protos/squareup/common/currency.pb'
import { CurrencyCode } from '@cash-web/protos/squareup/common/currency_codes.pb'
import { Instrument } from '@cash-web/protos/squareup/franklin/api/instrument.pb'
import { CashInstrumentType } from '@cash-web/protos/squareup/franklin/cash_instrument_type.pb'
import { ExchangeRate } from '@cash-web/protos/squareup/franklin/common/balance_data.pb'
import { DepositPreferenceOption } from '@cash-web/protos/squareup/franklin/common/deposit_preference.pb'
import { Profile } from '@cash-web/protos/squareup/franklin/ProfileProto.pb'
import { RatePlan } from '@cash-web/protos/squareup/franklin/rate_plan.pb'

const isCashBalanceNotBitcoin = (instrument: Instrument) =>
  instrument.cash_instrument_type === CashInstrumentType.CASH_BALANCE &&
  instrument.available_balance?.currency_code !== CurrencyCode.BTC

const isDebitCard = (instrument: Instrument) => {
  return instrument.cash_instrument_type === CashInstrumentType.DEBIT_CARD
}

const isBankAccount = (instrument: Instrument) => {
  return instrument.cash_instrument_type === CashInstrumentType.BANK_ACCOUNT
}

export const storageInstrument = (instruments: Instrument[]) => instruments.find(isCashBalanceNotBitcoin)

export const findDebitCardInstrument = (instruments: Instrument[]) => instruments.find(isDebitCard)

export const findBankAccountInstrument = (instruments: Instrument[]) => instruments.find(isBankAccount)

export const transferSourceInstruments = (profile: Profile | undefined): Instrument[] => {
  const instruments = profile?.instruments ?? []

  return instruments.filter(instrument => instrument?.supports_cash_in)
}

// Cash Out destinations, transfer targets of the storage instrument
export const transferTargetInstruments = (profile: Profile) => {
  const instruments = profile.instruments ?? []
  const transferInstruments = profile.balance_data?.supported_transfer_instruments ?? []

  const balanceInstrumentToken = storageInstrument(instruments)?.token
  const balanceInstrument = transferInstruments.find(
    ({ source_instrument_token }) => source_instrument_token === balanceInstrumentToken
  )
  if (!balanceInstrument) return []

  return instruments.filter(({ token }) => token && balanceInstrument.target_instrument_tokens?.includes(token))
}

const NO_STORAGE_INSTRUMENT = { amount: 0, currency_code: CurrencyCode.USD }
export const getBalance = (instruments: Instrument[] = []): Money =>
  storageInstrument(instruments)?.available_balance ?? NO_STORAGE_INSTRUMENT

export const getCashOutOptions = (profile: Profile): DepositPreferenceOption[] => {
  if (!isBusinessAccount(profile.rate_plan) && !profile.deposit_preference_data?.cash_out_options) {
    throw new Error('No withdraw options found')
  }
  return profile.deposit_preference_data?.cash_out_options ?? []
}

export const isBusinessAccount = (ratePlan: RatePlan | undefined) => {
  const businessRatePlans: RatePlan[] = [
    RatePlan.BUSINESS,
    RatePlan.BUSINESS_0,
    RatePlan.BUSINESS_1_5,
    RatePlan.BUSINESS_1_9,
    RatePlan.BUSINESS_2_60,
    RatePlan.BUSINESS_2_75,
    RatePlan.BUSINESS_MERCHANT,
    RatePlan.BUSINESS_NONPROFIT,
  ]
  return !!ratePlan && businessRatePlans.includes(ratePlan)
}

const isBitcoinBalance = (instrument: Instrument) =>
  instrument.cash_instrument_type === CashInstrumentType.CASH_BALANCE &&
  instrument.available_balance?.currency_code === CurrencyCode.BTC

export const bitcoinStorageInstrument = (instruments: Instrument[]) => instruments.find(isBitcoinBalance)

export const getBitcoinBalanceInUsd = (availableBalance: number, btcToUsdExchangeRate: number): Money => {
  // Exchange rate doesn't change for satoshis to BTC (1 satoshi = 0.00000001 BTC)
  const sat_to_btc_exchange_rate = 0.00000001
  const btc_as_usd_amount = availableBalance * sat_to_btc_exchange_rate * btcToUsdExchangeRate

  return {
    amount: btc_as_usd_amount,
    currency_code: CurrencyCode.USD,
  }
}

export const getBitcoinExchangeRatePercentage = (exchangeRate?: ExchangeRate): number => {
  if (!exchangeRate) {
    return 0
  }

  const { base_value_cents: baseValue = 0, change_cents: changeValue = 0 } = exchangeRate
  return (changeValue / (baseValue - changeValue)) * 100
}

export const hasAdoptedBitcoin = (profile?: GetProfileInfoResponse) => {
  return profile?.bitcoin_balance?.snapshot?.amount != null
}
